import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Cpu, Atom, Zap, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PROVIDER_COLORS = {
  openai: "#3B82F6",
  anthropic: "#F97316",
  google: "#14B8A6",
  local: "#9CA3AF",
};

const MODEL_PROVIDERS = {
  "gpt-5.2": "openai", "gpt-4o": "openai", "gpt-4o-mini": "openai",
  "claude-sonnet": "anthropic", "claude-haiku": "anthropic",
  "gemini-3-pro": "google", "gemini-3-flash": "google",
  "local": "local",
};

const TIER_STYLES = {
  standard: { label: "S", bg: "bg-gray-500/20", text: "text-gray-400", border: "border-gray-500/30" },
  high: { label: "H", bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
  critical: { label: "C", bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
};

const NODE_W = 180;
const NODE_H = 80;
const GAP_X = 50;
const GAP_Y = 100;

function computeLayout(dagAnalysis, optimizedTasks) {
  if (!dagAnalysis?.parallel_groups?.length) return { nodes: [], edges: [], width: 0, height: 0 };

  const taskMap = {};
  (optimizedTasks || []).forEach(t => { taskMap[t.id] = t; });

  const nodes = [];
  const groups = dagAnalysis.parallel_groups;
  let maxGroupWidth = 1;
  groups.forEach(g => { if (g.tasks.length > maxGroupWidth) maxGroupWidth = g.tasks.length; });

  const totalWidth = maxGroupWidth * (NODE_W + GAP_X) - GAP_X + 80;

  groups.forEach((group, levelIdx) => {
    const count = group.tasks.length;
    const groupWidth = count * (NODE_W + GAP_X) - GAP_X;
    const startX = (totalWidth - groupWidth) / 2;
    const y = levelIdx * (NODE_H + GAP_Y) + 40;

    group.tasks.forEach((taskId, taskIdx) => {
      const x = startX + taskIdx * (NODE_W + GAP_X);
      const task = taskMap[taskId] || {};
      const provider = MODEL_PROVIDERS[task.optimized_agent] || "local";
      nodes.push({
        id: taskId,
        x, y,
        level: levelIdx,
        name: task.name || taskId,
        originalAgent: task.original_agent,
        optimizedAgent: task.optimized_agent,
        tokens: task.tokens,
        complexityTier: task.complexity_tier || "standard",
        provider,
        color: PROVIDER_COLORS[provider] || "#9CA3AF",
        isParallel: count > 1,
      });
    });
  });

  const edges = (dagAnalysis.dependency_edges || []).map(e => {
    const from = nodes.find(n => n.id === e.from);
    const to = nodes.find(n => n.id === e.to);
    if (!from || !to) return null;
    return {
      from: { x: from.x + NODE_W / 2, y: from.y + NODE_H },
      to: { x: to.x + NODE_W / 2, y: to.y },
      isCritical: (dagAnalysis.critical_path || []).includes(e.from) && (dagAnalysis.critical_path || []).includes(e.to),
      isForkJoin: from.isParallel || to.isParallel,
    };
  }).filter(Boolean);

  const height = groups.length * (NODE_H + GAP_Y) + 40;
  return { nodes, edges, width: totalWidth, height };
}

function computeSequentialLayout(tasks) {
  if (!tasks?.length) return { nodes: [], edges: [], width: 0, height: 0 };
  const totalWidth = NODE_W + 80;
  const nodes = tasks.map((t, i) => ({
    id: t.id,
    x: 40,
    y: i * (NODE_H + GAP_Y) + 40,
    level: i,
    name: t.name || t.id,
    originalAgent: t.original_agent || t.agent,
    optimizedAgent: null,
    tokens: t.tokens || t.estimated_tokens,
    complexityTier: null,
    provider: null,
    color: "#6B7280",
    isParallel: false,
  }));
  const edges = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      from: { x: nodes[i].x + NODE_W / 2, y: nodes[i].y + NODE_H },
      to: { x: nodes[i + 1].x + NODE_W / 2, y: nodes[i + 1].y },
      isCritical: false,
    });
  }
  const height = nodes.length * (NODE_H + GAP_Y) + 40;
  return { nodes, edges, width: totalWidth, height };
}

function CurvedEdge({ edge, animDelay, optimized, reducedMotion }) {
  const { from, to, isCritical, isForkJoin } = edge;
  const midY = (from.y + to.y) / 2;
  const d = `M${from.x},${from.y} C${from.x},${midY} ${to.x},${midY} ${to.x},${to.y}`;
  const animated = isForkJoin && optimized;
  const repeatCfg = reducedMotion ? 0 : Infinity;

  if (animated) {
    return (
      <>
        <motion.path
          d={d} fill="none" stroke="url(#fork-join-gradient)"
          strokeWidth={8} strokeLinecap="round" opacity={0.1}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: animDelay }}
        />
        <motion.path
          d={d} fill="none" stroke="url(#fork-join-gradient)"
          strokeWidth={2.5} strokeLinecap="round"
          strokeDasharray="8 4"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1, strokeDashoffset: [0, -24] }}
          transition={{
            pathLength: { duration: 0.6, delay: animDelay },
            opacity: { duration: 0.6, delay: animDelay },
            strokeDashoffset: { duration: 1.5, repeat: repeatCfg, ease: "linear", delay: animDelay + 0.7 },
          }}
        />
      </>
    );
  }

  return (
    <motion.path
      d={d}
      fill="none"
      stroke={isCritical && optimized ? "var(--qn-primary)" : "var(--qn-border)"}
      strokeWidth={isCritical && optimized ? 2.5 : 1.5}
      strokeDasharray={optimized ? "none" : "6 4"}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{ duration: 0.6, delay: animDelay }}
    />
  );
}

function DAGNode({ node, animDelay, optimized, showCompare, reducedMotion }) {
  const tier = TIER_STYLES[node.complexityTier] || TIER_STYLES.standard;
  const displayAgent = showCompare ? node.originalAgent : (node.optimizedAgent || node.originalAgent);
  const displayColor = optimized && !showCompare ? node.color : "#6B7280";

  return (
    <motion.g
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: animDelay }}
    >
      <title>{node.name}</title>
      <motion.rect
        x={node.x}
        y={node.y}
        width={NODE_W}
        height={NODE_H}
        rx={10}
        fill="var(--qn-surface)"
        stroke={displayColor}
        strokeWidth={2}
        initial={{ stroke: "#6B7280" }}
        animate={{ stroke: displayColor }}
        transition={{ duration: 0.5, delay: animDelay + 0.3 }}
      />
      {/* Task name */}
      <text
        x={node.x + 10}
        y={node.y + 22}
        fill="var(--qn-foreground)"
        fontSize={12}
        fontWeight="600"
        className="select-none"
      >
        {(node.name || "").length > 20 ? node.name.slice(0, 18) + "..." : node.name}
      </text>
      {/* Model label */}
      <motion.text
        x={node.x + 10}
        y={node.y + 42}
        fill={displayColor}
        fontSize={11}
        fontWeight="500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: animDelay + 0.5 }}
      >
        {displayAgent || "—"}
      </motion.text>
      {/* Tokens */}
      <text
        x={node.x + 10}
        y={node.y + 60}
        fill="var(--qn-foreground-subtle)"
        fontSize={10}
      >
        {node.tokens ? `${node.tokens} tokens` : ""}
      </text>
      {/* Complexity badge */}
      {optimized && node.complexityTier && !showCompare && (
        <motion.g
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: animDelay + 0.6, type: "spring" }}
        >
          <rect
            x={node.x + NODE_W - 28}
            y={node.y + 6}
            width={22}
            height={18}
            rx={4}
            fill={tier.label === "S" ? "rgba(107,114,128,0.25)" : tier.label === "H" ? "rgba(59,130,246,0.25)" : "rgba(249,115,22,0.25)"}
          />
          <text
            x={node.x + NODE_W - 17}
            y={node.y + 19}
            fontSize={11}
            fontWeight="bold"
            textAnchor="middle"
            fill={tier.label === "S" ? "#9CA3AF" : tier.label === "H" ? "#60A5FA" : "#FB923C"}
          >
            {tier.label}
          </text>
        </motion.g>
      )}
      {/* Parallel indicator — enhanced with ripple */}
      {node.isParallel && optimized && !showCompare && (
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: animDelay + 0.8 }}
        >
          <motion.circle
            cx={node.x + NODE_W - 10}
            cy={node.y + NODE_H - 10}
            fill="none" stroke="var(--qn-primary)" strokeWidth={1}
            initial={{ r: 4, opacity: 0 }}
            animate={{ r: [4, 12], opacity: [0.6, 0] }}
            transition={{ duration: 2, repeat: reducedMotion ? 0 : Infinity, ease: "easeOut", delay: animDelay + 1 }}
          />
          <motion.circle
            cx={node.x + NODE_W - 10}
            cy={node.y + NODE_H - 10}
            r={4}
            fill="var(--qn-primary)"
            animate={{ opacity: reducedMotion ? 1 : [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}
          />
        </motion.g>
      )}
    </motion.g>
  );
}

export default function ParallelDAG({ dagAnalysis, optimizedTasks, tasks, optimized, solverInfo }) {
  const [showCompare, setShowCompare] = useState(false);
  const [animPhase, setAnimPhase] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  const optimizedLayout = useMemo(
    () => computeLayout(dagAnalysis, optimizedTasks),
    [dagAnalysis, optimizedTasks]
  );
  const seqLayout = useMemo(
    () => computeSequentialLayout(tasks),
    [tasks]
  );

  const layout = optimized && !showCompare ? optimizedLayout : seqLayout;

  useEffect(() => {
    if (optimized) {
      const t1 = setTimeout(() => setAnimPhase(1), 300);
      const t2 = setTimeout(() => setAnimPhase(2), 900);
      const t3 = setTimeout(() => setAnimPhase(3), 1800);
      const t4 = setTimeout(() => setAnimPhase(4), 2500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    }
    setAnimPhase(0);
  }, [optimized]);

  if (!tasks?.length && !optimizedTasks?.length) {
    return (
      <div className="text-center py-12 text-[var(--qn-foreground-subtle)]" data-testid="dag-empty">
        No workflow loaded. Create a demo workflow to see the DAG visualization.
      </div>
    );
  }

  const da = dagAnalysis || {};
  // Auto-fit: add padding for decorations (parallel badges, corner dots, glow effects)
  const PAD = 40;
  const vbW = layout.width + PAD * 2;
  const vbH = layout.height + PAD * 2;

  return (
    <div className="space-y-4" data-testid="parallel-dag-container">
      {/* Animation phase indicator */}
      {optimized && animPhase > 0 && animPhase < 4 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="text-center"
        >
          <span className="text-sm text-[var(--qn-primary)] font-medium animate-pulse">
            {animPhase === 1 && "Analyzing task dependencies..."}
            {animPhase === 2 && `Restructuring DAG — found ${da.max_concurrent_tasks || 0} parallel tasks`}
            {animPhase === 3 && "Assigning optimal models per complexity tier..."}
          </span>
        </motion.div>
      )}

      {/* SVG DAG — auto-fits to container via viewBox */}
      <div className="overflow-hidden rounded-xl border border-[var(--qn-border)] bg-[var(--qn-background)] p-3" data-testid="dag-container" style={{ minHeight: 400, maxHeight: '80vh' }}>
        <svg
          width="100%"
          viewBox={`${-PAD} ${-PAD} ${vbW} ${vbH}`}
          preserveAspectRatio="xMidYMid meet"
          className="mx-auto"
          style={{ maxHeight: '78vh', minHeight: 380 }}
          data-testid="dag-svg"
        >
          <defs>
            <linearGradient id="parallel-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--qn-primary)" />
              <stop offset="100%" stopColor="var(--qn-secondary)" />
            </linearGradient>
            <linearGradient id="fork-join-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--qn-primary)" />
              <stop offset="100%" stopColor="var(--qn-secondary)" />
            </linearGradient>
            {!prefersReducedMotion && (
              <linearGradient id="badge-shimmer" x1="-1" y1="0" x2="0" y2="0" gradientUnits="objectBoundingBox">
                <stop offset="0" stopColor="white" stopOpacity="0" />
                <stop offset="0.5" stopColor="white" stopOpacity="0.3" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
                <animate attributeName="x1" values="-1;2" dur="2.5s" repeatCount="indefinite" />
                <animate attributeName="x2" values="0;3" dur="2.5s" repeatCount="indefinite" />
              </linearGradient>
            )}
          </defs>
          {/* Edges */}
          {layout.edges.map((edge, i) => (
            <CurvedEdge
              key={`edge-${i}`}
              edge={edge}
              animDelay={optimized ? 0.5 + i * 0.1 : i * 0.1}
              optimized={optimized && !showCompare}
              reducedMotion={prefersReducedMotion}
            />
          ))}
          {/* Nodes */}
          {layout.nodes.map((node, i) => (
            <DAGNode
              key={node.id}
              node={node}
              animDelay={optimized ? 0.2 + i * 0.08 : i * 0.1}
              optimized={optimized}
              showCompare={showCompare}
              reducedMotion={prefersReducedMotion}
            />
          ))}
          {/* Parallel group brackets — animated gradient borders */}
          {optimized && !showCompare && (da.parallel_groups || []).map((group, idx) => {
            if (!group.can_parallelize) return null;
            const groupNodes = layout.nodes.filter(n => group.tasks.includes(n.id));
            if (groupNodes.length < 2) return null;
            const minX = Math.min(...groupNodes.map(n => n.x)) - 16;
            const maxX = Math.max(...groupNodes.map(n => n.x + NODE_W)) + 16;
            const gY = groupNodes[0].y - 14;
            const gW = maxX - minX;
            const gH = NODE_H + 28;
            const corners = [[minX, gY], [minX + gW, gY], [minX, gY + gH], [minX + gW, gY + gH]];
            const rpt = prefersReducedMotion ? 0 : Infinity;
            return (
              <motion.g key={`bracket-${idx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 + idx * 0.15, duration: 0.6 }}>
                {/* Gradient border with animated flowing dashes */}
                <motion.rect
                  x={minX} y={gY} width={gW} height={gH} rx={12}
                  fill="none" stroke="url(#parallel-gradient)" strokeWidth={2}
                  strokeDasharray="12 6"
                  initial={{ strokeDashoffset: 0 }}
                  animate={{ strokeDashoffset: -36 }}
                  transition={{ duration: 3, repeat: rpt, ease: "linear" }}
                />
                {/* Soft glow behind border */}
                <rect
                  x={minX} y={gY} width={gW} height={gH} rx={12}
                  fill="none" stroke="url(#parallel-gradient)" strokeWidth={6} opacity={0.08}
                />
                {/* Animated corner energy dots */}
                {corners.map(([cx, cy], di) => (
                  <motion.circle
                    key={di} cx={cx} cy={cy}
                    fill="var(--qn-primary)"
                    initial={{ r: 0, opacity: 0 }}
                    animate={{ r: [3, 5, 3], opacity: [0.4, 1, 0.4] }}
                    transition={{ delay: 1.5 + idx * 0.15 + di * 0.08, duration: 2, repeat: rpt, ease: "easeInOut" }}
                  />
                ))}
                {/* Animated PARALLEL badge */}
                <motion.g
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 + idx * 0.15, type: "spring", stiffness: 200 }}
                  data-testid={`parallel-badge-${idx}`}
                >
                  <rect
                    x={minX + gW / 2 - 44} y={gY - 12}
                    width={88} height={22} rx={11}
                    fill="url(#parallel-gradient)"
                  />
                  {!prefersReducedMotion && (
                    <rect
                      x={minX + gW / 2 - 44} y={gY - 12}
                      width={88} height={22} rx={11}
                      fill="url(#badge-shimmer)"
                    />
                  )}
                  <line x1={minX + gW / 2 - 30} y1={gY - 6} x2={minX + gW / 2 - 30} y2={gY + 4} stroke="white" strokeWidth={1.5} strokeLinecap="round" opacity={0.9} />
                  <line x1={minX + gW / 2 - 26} y1={gY - 6} x2={minX + gW / 2 - 26} y2={gY + 4} stroke="white" strokeWidth={1.5} strokeLinecap="round" opacity={0.9} />
                  <text
                    x={minX + gW / 2 + 6} y={gY + 3}
                    fontSize={9} fontWeight="700" textAnchor="middle" fill="white"
                    style={{ letterSpacing: '1.5px' }}
                  >
                    PARALLEL
                  </text>
                </motion.g>
              </motion.g>
            );
          })}
        </svg>
      </div>

      {/* Bottom stats bar */}
      {optimized && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0 }}
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]"
          data-testid="dag-stats-bar"
        >
          <div className="flex items-center gap-4 text-sm">
            {da.time_saved_by_parallelization_pct > 0 && (
              <span className="text-[var(--qn-foreground-muted)]">
                Time: <span className="line-through opacity-50">{(da.original_sequential_time_ms / 1000).toFixed(1)}s</span>
                {" "}<span className="text-[var(--qn-success)] font-semibold">{(da.parallelized_time_ms / 1000).toFixed(1)}s</span>
                <span className="text-[var(--qn-success)] ml-1">({da.time_saved_by_parallelization_pct}% faster)</span>
              </span>
            )}
            <span className="text-[var(--qn-foreground-subtle)]">
              Groups: {(da.parallel_groups || []).length} | Max Concurrent: {da.max_concurrent_tasks || 1}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {solverInfo && (
              <span className="text-xs text-[var(--qn-foreground-subtle)] font-mono" data-testid="dag-solver-info">
                {solverInfo.solver_type === "quantum_cqm" ? (
                  <><Atom className="w-3 h-3 inline mr-1" />Agentic optimization via Quantum CQM in {solverInfo.time}ms (QPU: {(solverInfo.qpu / 1000).toFixed(1)}ms)</>
                ) : solverInfo.solver_type === "classical_greedy" ? (
                  <><Zap className="w-3 h-3 inline mr-1" />Agentic optimization via Classical Greedy in {solverInfo.time}ms</>
                ) : (
                  <><Cpu className="w-3 h-3 inline mr-1" />Agentic optimization via Classical SA in {solverInfo.time}ms</>
                )}
              </span>
            )}
            <button
              onClick={() => setShowCompare(v => !v)}
              className="text-xs px-2.5 py-1 rounded-md border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:bg-[var(--qn-surface)] transition-colors"
              data-testid="dag-compare-toggle"
            >
              {showCompare ? "Show Optimized" : "Compare Before"}
            </button>
          </div>
        </motion.div>
      )}

      {/* Legend */}
      {optimized && !showCompare && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2 }}
          className="flex flex-wrap gap-4 px-4 text-xs text-[var(--qn-foreground-subtle)]"
        >
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PROVIDER_COLORS.openai }} /> OpenAI</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PROVIDER_COLORS.anthropic }} /> Anthropic</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PROVIDER_COLORS.google }} /> Google</span>
          <span className="mx-2 opacity-30">|</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 text-center text-[10px] font-bold text-gray-400 bg-gray-500/20 rounded px-0.5">S</span> Standard</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 text-center text-[10px] font-bold text-blue-400 bg-blue-500/20 rounded px-0.5">H</span> High</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 text-center text-[10px] font-bold text-orange-400 bg-orange-500/20 rounded px-0.5">C</span> Critical</span>
        </motion.div>
      )}
    </div>
  );
}
